package com.iptvplayer.shared.data.remote.xtream

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class XtreamAuthResponse(
    @SerialName("user_info") val userInfo: XtreamUserInfo = XtreamUserInfo(),
    @SerialName("server_info") val serverInfo: XtreamServerInfo = XtreamServerInfo()
)

@Serializable
data class XtreamUserInfo(
    val username: String = "",
    val password: String = "",
    val status: String = "",
    @SerialName("exp_date") val expDate: String = "",
    @SerialName("is_trial") val isTrial: String = "0",
    @SerialName("active_cons") val activeCons: String = "0",
    @SerialName("created_at") val createdAt: String = "",
    @SerialName("max_connections") val maxConnections: String = "1",
    @SerialName("allowed_output_formats") val allowedOutputFormats: List<String> = emptyList()
)

@Serializable
data class XtreamServerInfo(
    val url: String = "",
    val port: String = "",
    @SerialName("https_port") val httpsPort: String = "",
    @SerialName("server_protocol") val serverProtocol: String = "http",
    @SerialName("rtmp_port") val rtmpPort: String = "",
    val timezone: String = "",
    @SerialName("timestamp_now") val timestampNow: Long = 0L,
    @SerialName("time_now") val timeNow: String = ""
)

@Serializable
data class XtreamCategory(
    @SerialName("category_id") val categoryId: String = "",
    @SerialName("category_name") val categoryName: String = "",
    @SerialName("parent_id") val parentId: Int = 0
)

@Serializable
data class XtreamLiveStream(
    val num: Int? = null,
    val name: String = "",
    @SerialName("stream_id") val streamId: Int = 0,
    @SerialName("stream_icon") val streamIcon: String? = null,
    @SerialName("epg_channel_id") val epgChannelId: String? = null,
    @SerialName("added") val added: String? = null,
    @SerialName("category_id") val categoryId: String? = null,
    @SerialName("category_name") val categoryName: String? = null,
    @SerialName("custom_sid") val customSid: String? = null,
    @SerialName("tv_archive") val tvArchive: Int? = null,
    @SerialName("direct_source") val directSource: String? = null,
    @SerialName("tv_archive_duration") val tvArchiveDuration: Int? = null
)

@Serializable
data class XtreamSeries(
    val num: Int? = null,
    val name: String = "",
    @SerialName("series_id") val seriesId: Int = 0,
    val cover: String? = null,
    val plot: String? = null,
    val cast: String? = null,
    val director: String? = null,
    val genre: String? = null,
    val releaseDate: String? = null,
    @SerialName("last_modified") val lastModified: String? = null,
    val rating: String? = null,
    // rating_5based may come as int or string across panels; ignore it (we use `rating`).
    @SerialName("category_id") val categoryId: String? = null,
    @SerialName("category_name") val categoryName: String? = null
)

@Serializable
data class XtreamSeasonInfo(
    val id: Int? = null,
    @SerialName("season_number") val seasonNumber: Int = 0,
    val name: String? = null,
    val overview: String? = null,
    @SerialName("cover_big") val coverBig: String? = null
)

@Serializable
data class XtreamEpisodeInfo(
    val plot: String? = null,
    @SerialName("duration_secs") val durationSecs: Long? = null,
    @SerialName("movie_image") val movieImage: String? = null,
    val releasedate: String? = null
)

@Serializable
data class XtreamEpisode(
    val id: String = "",
    // episode_num/season come as int on some panels and string on others;
    // IntOrStringAsString accepts both and gives us a normalized String.
    @SerialName("episode_num")
    @Serializable(with = IntOrStringAsString::class)
    val episodeNum: String = "0",
    val title: String = "",
    @SerialName("container_extension") val containerExtension: String? = null,
    val info: XtreamEpisodeInfo = XtreamEpisodeInfo(),
    @Serializable(with = IntOrStringAsString::class)
    val season: String = "0"
)

@Serializable
data class XtreamSeriesInfoResponse(
    val seasons: List<XtreamSeasonInfo> = emptyList(),
    // "episodes" is keyed by season number as a string ("1", "2", …); a few
    // panels send `[]` instead when there are none — handled by the serializer.
    @Serializable(with = EpisodesMapSerializer::class)
    val episodes: Map<String, List<XtreamEpisode>> = emptyMap()
)

@Serializable
data class XtreamShortEpgResponse(
    @SerialName("epg_listings") val epgListings: List<XtreamEpgListing> = emptyList()
)

/**
 * One `get_short_epg` listing. `title`/`description` are base64-encoded by the
 * panel; timestamps are epoch **seconds**. Everything is kept as String —
 * panels disagree on number-vs-string types and the lenient Json handles both.
 */
@Serializable
data class XtreamEpgListing(
    val id: String? = null,
    @SerialName("epg_id") val epgId: String? = null,
    val title: String = "",
    val description: String = "",
    @SerialName("start_timestamp") val startTimestamp: String = "",
    @SerialName("stop_timestamp") val stopTimestamp: String = "",
    val start: String? = null,
    val end: String? = null,
    @SerialName("now_playing") val nowPlaying: Int? = null
)

@Serializable
data class XtreamVodStream(
    val num: Int? = null,
    val name: String = "",
    @SerialName("stream_id") val streamId: Int = 0,
    @SerialName("stream_icon") val streamIcon: String? = null,
    val rating: String? = null,
    @SerialName("rating_5based") val rating5based: String? = null,
    val added: String? = null,
    @SerialName("category_id") val categoryId: String? = null,
    @SerialName("category_name") val categoryName: String? = null,
    @SerialName("container_extension") val containerExtension: String? = null,
    @SerialName("custom_sid") val customSid: String? = null,
    @SerialName("direct_source") val directSource: String? = null,
    val plot: String? = null,
    val cast: String? = null,
    val director: String? = null,
    val genre: String? = null,
    @SerialName("release_date") val releaseDate: String? = null,
    val duration: String? = null
)
